'use client';

import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useCreateEntity } from '@/lib/hooks/useEntities';
import { getEntityTypeLabel } from '@sizzlenz/shared';
import { EntityForm, type EntityFormRef } from './EntityForm';
import { applyApiFieldError } from '@/lib/hooks/useApiFormError';
import { useSession } from '@/lib/auth-client';

interface NewEntityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  allowedTypes?: string[];
  defaultType?: string;
  title?: string;
  explainer?: React.ReactNode;
  /** When provided, the form will be prepopulated with values from this entity (clone mode) */
  cloneFrom?: import('@/lib/api/types').ApiEntity;
}

export function NewEntityDialog({
  isOpen,
  onClose,
  allowedTypes = ['organisation', 'person', 'group', 'space'],
  defaultType = 'organisation',
  title,
  explainer,
  cloneFrom,
}: NewEntityDialogProps) {
  const createEntity = useCreateEntity();
  const [type, setType] = useState(cloneFrom?.type || defaultType);
  const formRef = useRef<EntityFormRef>(null);
  const [formKey, setFormKey] = useState(0);
  const { data: sessionData } = useSession();
  const isPlatformAdmin = !!sessionData?.user?.isAdmin;

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to ensure Radix has finished its cleanup
      const timer = setTimeout(() => {
        setType(defaultType);
        setFormKey((prev) => prev + 1); // Force form reset
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, defaultType]);

  const handleCreate = () => {
    formRef.current?.submit();
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      // Build links array from social media URLs
      const links = [];
      if (formData.instagram_url?.trim()) {
        links.push({ platform: 'instagram', url: formData.instagram_url.trim() });
      }
      if (formData.linkedin_url?.trim()) {
        links.push({ platform: 'linkedin', url: formData.linkedin_url.trim() });
      }
      if (formData.facebook_url?.trim()) {
        links.push({ platform: 'facebook', url: formData.facebook_url.trim() });
      }
      if (formData.tiktok_url?.trim()) {
        links.push({ platform: 'tiktok', url: formData.tiktok_url.trim() });
      }

      // Build data object
      const data: any = {};
      if (links.length > 0) {
        data.links = links;
      }

      // Contact details (stored in entity.data.contactDetails)
      data.contactDetails = {
        name: formData.contactDetailsName?.trim() || undefined,
        email: formData.contactDetailsEmail?.trim() || undefined,
        phone: formData.contactDetailsPhone?.trim() || undefined,
      };

      if (formData.roles && formData.roles.length > 0) {
        data.roles = formData.roles;
      }

      if (formData.capabilities && formData.capabilities.length > 0) {
        data.capabilities = formData.capabilities;
      }

      if (formData.specialisations && formData.specialisations.length > 0) {
        data.specialisations = formData.specialisations;
      }

      if (formData.channelExpertise && formData.channelExpertise.length > 0) {
        data.channelExpertise = formData.channelExpertise;
      }

      if (formData.geoPresence && formData.geoPresence.length > 0) {
        data.geoPresence = formData.geoPresence;
      }

      if (formData.geoExpertise && formData.geoExpertise.length > 0) {
        data.geoExpertise = formData.geoExpertise;
      }

      if (formData.galleryImages && formData.galleryImages.length > 0) {
        data.gallery = {
          images: formData.galleryImages,
        };
      }
      if (formData.showInitiatives !== undefined) {
        data.main_page = {
          showInitiatives: formData.showInitiatives,
          showItems: formData.showItems,
          showEnquiryForm: formData.showEnquiryForm ?? false,
          showContactDetails: formData.showContactDetails ?? false,
          showContentFeed: formData.showContentFeed ?? false,
          contentFeedTitle: formData.contentFeedTitle || '',
        };
      }
      if (
        formData.initiativesSectionTitle ||
        formData.initiativesDescription ||
        formData.allowPartnerProposals !== undefined
      ) {
        data.initiatives = {
          sectionTitle: formData.initiativesSectionTitle || '',
          description: formData.initiativesDescription || '',
          allowPartnerProposals: formData.allowPartnerProposals ?? false,
        };
      }
      data.items = {
        sectionTitle: formData.itemsSectionTitle || '',
      };
      if (formData.trade_enabled !== undefined) {
        data.trade = {
          enabled: formData.trade_enabled,
        };
      }
      if (formData.hosts_activations !== undefined || formData.runs_activations !== undefined) {
        data.activations = {
          hosts: formData.hosts_activations ?? false,
          runs: formData.runs_activations ?? false,
        };
      }
      if (formData.base_region || formData.regions_served) {
        data.reporting = {
          base_region: formData.base_region,
          regions_served: formData.regions_served,
        };
      }
      if (formData.exportsTo && formData.exportsTo.length > 0) {
        data.exports_to = formData.exportsTo;
      }

      // Handle address_1: store place detail in data, backend syncs geography column
      if (formData.address_1 !== undefined) {
        data.address_1 = formData.address_1;
      }

      // Engagement settings
      data.allowReactions = formData.allowReactions !== false;
      data.allowComments = formData.allowComments !== false;

      // Themes
      if (formData.themes && formData.themes.length > 0) {
        data.themes = formData.themes;
      }
      if (formData.themeFeatured !== undefined) {
        data.themeFeatured = formData.themeFeatured;
      }
      if (formData.featuredPartner !== undefined) {
        data.featuredPartner = formData.featuredPartner;
      }

      // Parse tags from comma-separated string
      const tags = formData.tags
        ? formData.tags
            .split(',')
            .map((t: string) => t.trim())
            .filter((t: string) => t.length > 0)
        : [];

      createEntity.mutate(
        {
          type,
          name_1: formData.name_1.trim(),
          slug: formData.slug?.trim() || null,
          description: formData.description?.trim() || null,
          image: formData.image || null,
          website: formData.website?.trim() || null,
          primary_email: formData.primary_email?.trim() || null,
          primary_phone: formData.primary_phone?.trim() || null,
          status: 'active',
          visibility: formData.visibility || 'visible',
          tags,
          ...(Object.keys(data).length > 0 && { data }),
          ...(isPlatformAdmin &&
            formData.notes?.trim() && {
              systemData: { notes: formData.notes.trim() },
            }),
        } as any,
        {
          onSuccess: () => {
            toast.success(`${getEntityTypeLabel(type)} created successfully`);
            onClose();
          },
          onError: (error: any) => {
            console.error('Failed to create entity:', error);
            const handled = applyApiFieldError(error, formRef.current?.setError);
            if (!handled) {
              toast.error(error?.message || 'Failed to create entity');
            }
          },
        }
      );
    } catch (error: any) {
      console.error('Failed to create entity:', error);
      toast.error(error?.message || 'Failed to create entity');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] flex flex-col max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold text-orange-500">
            {title || 'Create New Entity'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {explainer && <div className="mb-4">{explainer}</div>}
          <div className="space-y-4">
            {/* Type Selector */}
            {allowedTypes.length > 1 && (
              <div>
                <Label htmlFor="type">
                  Type <span className="text-red-500">*</span>
                </Label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer w-full"
                >
                  {allowedTypes.map((t) => (
                    <option key={t} value={t}>
                      {getEntityTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Entity Form */}
            <EntityForm
              key={formKey}
              ref={formRef}
              entity={cloneFrom ? ({ ...cloneFrom, slug: '' } as any) : undefined}
              onSubmit={handleFormSubmit}
              showRoles
              isPlatformAdmin={isPlatformAdmin}
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="ghost" onClick={onClose} disabled={createEntity.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            disabled={createEntity.isPending}
          >
            {createEntity.isPending ? 'Creating...' : `Create ${getEntityTypeLabel(type)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
